import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Box, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Draggable from "react-draggable";
import { SlotsInjector, useMediaQuery } from "../../utils";
import { draggablePopperSlots } from "./types";
const getPosition = (anchorEl, placement) => {
    if (!anchorEl)
        return { x: 0, y: 0 };
    const { width, height, x, y } = anchorEl.getBoundingClientRect();
    switch (placement) {
        case "top":
            return { x: x, y: y };
        case "bottom":
            return { x: x, y: y + height };
        case "left":
            return { x: x, y: y };
        case "right":
            return { x: x + width, y: y };
        case "top-start":
            return { x: x, y: y };
        case "top-end":
            return { x: x + width, y: y };
        case "bottom-start":
            return { x: x, y: y + height };
        case "bottom-end":
            return { x: x + width, y: y + height };
        case "right-start":
            return { x: x + width, y: y };
        case "right-end":
            return { x: x + width, y: y + height };
        case "left-start":
            return { x: x, y: y };
        case "left-end":
            return { x: x, y: y + height };
        default:
            return { x: 0, y: 0 };
    }
};
/**
 * Inverts the scale of the DraggablePopper based on the placement prop. Allows for more
 * complex placement options while making position calculations easier. This function is
 * used to invert the root div while the immediate child of the popper reverses the
 * inversion to ensure the dragging axis is not also inverted.
 */
const getScaleTransform = (placement) => ({
    x: placement === "left" ||
        placement === "left-start" ||
        placement === "left-end" ||
        placement === "top-end" ||
        placement === "bottom-end"
        ? -1
        : 1,
    y: placement === "top" ||
        placement === "top-start" ||
        placement === "top-end" ||
        placement === "right-end" ||
        placement === "left-end"
        ? -1
        : 1,
});
/**
 * @description Similar to the popper component, the DraggablePopper is used to display a popover that anchors to a specific element onClick and allows the user to drag it anywhere on screen. As apposed to the DraggablePopperProvider, the DraggablePopper component requires the open and anchorEl state to be managed by the consuming component.
 * @remarks
 * - The DraggablePopper component does not auto position itself to avoid being clipped by the viewport. This is the responsibility of the consuming component via the placement prop.
 */
export const DraggablePopper = ({ anchorEl, placement = "bottom", ...props }) => {
    const mobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const currentPlacement = typeof placement === "string"
        ? placement
        : mobile
            ? placement.xs
            : placement.md;
    const [dragging, setDragging] = useState(false);
    const initialPosition = getPosition(anchorEl, currentPlacement);
    const [position, setPosition] = useState(initialPosition);
    const transform = getScaleTransform(currentPlacement);
    useEffect(() => {
        setPosition(getPosition(anchorEl, currentPlacement));
    }, [anchorEl, currentPlacement]);
    return (_jsx(SlotsInjector, { props: props, children: ({ children, open, onClose, slots, slotProps, getSlotsWithDefaults, sx, ...componentProps }) => {
            const { dragIcon: DragIcon, closeIcon: CloseIcon, dragButton: DragButton, closeButton: CloseButton, } = getSlotsWithDefaults(draggablePopperSlots, slots);
            return open && anchorEl && window
                ? createPortal(_jsx(Draggable, { handle: "#drag-handle", onStart: () => setDragging(true), onStop: () => setDragging(false), onDrag: (_e, { x, y }) => setPosition({ x, y }), position: position, 
                    //Comment out defaultPosition if a remembered position is desired
                    defaultPosition: position, children: _jsx(Box, { "data-component-name": "DraggablePopper", sx: {
                            position: "fixed",
                            left: position.x,
                            top: position.y,
                            transform: "none !important",
                            transformOrigin: "top left",
                            scale: `${transform.x} ${transform.y}`,
                            zIndex: (theme) => theme.zIndex.popper,
                        }, children: _jsxs(Stack, { ...componentProps, sx: {
                                scale: `${transform.x} ${transform.y}`,
                                display: "flex",
                                flexDirection: "column",
                                minWidth: (theme) => theme.spacing(39),
                                minHeight: (theme) => theme.spacing(58),
                                maxHeight: "80dvh",
                                border: 2,
                                borderRadius: 0.5,
                                borderColor: (theme) => dragging
                                    ? theme.palette.background
                                        .onMain
                                    : "transparent",
                                backgroundColor: (theme) => theme.palette.background.main,
                                boxShadow: (theme) => theme.elevation.bottom.level2,
                                ...sx,
                            }, children: [_jsxs(Stack, { flexDirection: "row", gap: 1, padding: 0.5, justifyContent: "space-between", children: [_jsx(DragButton, { id: "drag-handle", ...slotProps?.dragButton, sx: {
                                                cursor: dragging
                                                    ? "grabbing"
                                                    : "grab",
                                                ...slotProps?.dragButton?.sx,
                                            }, children: _jsx(DragIcon, { ...slotProps?.dragIcon }) }), _jsx(CloseButton, { onClick: onClose, ...slotProps?.closeButton, children: _jsx(CloseIcon, { ...slotProps?.closeIcon }) })] }), _jsx(Box, { sx: {
                                        display: "flex",
                                        flexGrow: 1,
                                        overflow: "auto",
                                    }, children: children })] }) }) }), document.body)
                : null;
        } }));
};
//# sourceMappingURL=DraggablePopper.js.map